import axios from 'axios';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { cognitoConfig } from '../config/cognito';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.userPoolWebClientId,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cognito_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for login endpoints - let the form handle the error
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = requestUrl.includes('/cognito-auth/login') || 
                            requestUrl.includes('/auth/login');
      
      if (!isAuthEndpoint) {
        localStorage.removeItem('cognito_access_token');
        localStorage.removeItem('cognito_refresh_token');
        localStorage.removeItem('cognito_id_token');
        localStorage.removeItem('user');
        // Force a full page reload to index.html which will handle routing
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export interface CognitoAuthResponse {
  token: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    groups?: string[];
  };
}

export interface LoginData {
  email: string;
  password: string;
}

export interface InviteUserData {
  email: string;
  first_name: string;
  last_name: string;
  is_admin?: boolean;
}

export const cognitoAuthApi = {
  login: async (data: LoginData): Promise<CognitoAuthResponse> => {
    try {
      // Use the backend API which handles Cognito authentication
      const response = await api.post('/cognito-auth/login', data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getCurrentUser: async () => {
    const response = await api.get('/cognito-auth/me');
    return response.data;
  },

  inviteUser: async (data: InviteUserData) => {
    const response = await api.post('/cognito-auth/invite', data);
    return response.data;
  },

  setPassword: async (email: string, password: string) => {
    const response = await api.post('/cognito-auth/set-password', { email, password });
    return response.data;
  },

  completePasswordChallenge: async (data: { email: string; session: string; newPassword: string }) => {
    const response = await api.post('/cognito-auth/respond-to-challenge', data);
    return response.data;
  },

  forgotPassword: async (email: string) => {
    try {
      const response = await api.post('/cognito-auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  confirmPassword: async (email: string, verificationCode: string, newPassword: string) => {
    try {
      const response = await api.post('/cognito-auth/confirm-password', {
        email,
        verificationCode,
        newPassword
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Direct Cognito operations for better UX
  loginDirect: (email: string, password: string): Promise<CognitoAuthResponse> => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();
          const idToken = result.getIdToken().getJwtToken();

          // Store tokens
          localStorage.setItem('cognito_access_token', accessToken);
          localStorage.setItem('cognito_refresh_token', refreshToken);
          localStorage.setItem('cognito_id_token', idToken);

          // Extract user info from ID token
          const idPayload = result.getIdToken().payload;
          const user = {
            id: idPayload.sub,
            email: idPayload.email,
            first_name: idPayload.given_name || '',
            last_name: idPayload.family_name || '',
            groups: idPayload['cognito:groups'] || [],
          };

          resolve({
            token: accessToken,
            refreshToken: refreshToken,
            user,
          });
        },
        onFailure: (error) => {
          console.error('Cognito login error:', error);
          reject(error);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // User needs to set a new password
          reject(new Error('NEW_PASSWORD_REQUIRED'));
        },
      });
    });
  },

  logout: () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    
    // Clear all tokens
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('user');
  },

  getCurrentUserDirect: (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No current user'));
        return;
      }

      cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session.isValid()) {
          reject(new Error('Session is not valid'));
          return;
        }

        cognitoUser.getUserAttributes((err: any, attributes: any) => {
          if (err) {
            reject(err);
            return;
          }

          const user = {
            id: cognitoUser.getUsername(),
            email: attributes.find((attr: any) => attr.Name === 'email')?.Value || '',
            first_name: attributes.find((attr: any) => attr.Name === 'given_name')?.Value || '',
            last_name: attributes.find((attr: any) => attr.Name === 'family_name')?.Value || '',
          };

          resolve(user);
        });
      });
    });
  },
};

export default api;